import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api_endpoints from "../../api";
import { getPageFromName } from "../../route_utils";
import FormController from "../../widgets/FormController/FormController";
import { TextField, Divider, Tabs, Tab, Chip } from "@mui/material";
import { useSuccessAlert } from "../../context/SuccessAlertContext/SuccessAlertContext";
import Autocomplete from "@mui/material/Autocomplete";
import { useNavigate } from "react-router-dom";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import ButtonLoading from "../../widgets/ButtonLoading/ButtonLoading";
import CancelButton from "../../widgets/CancelButton/CancelButton";
import TabWrapper from "../../widgets/TabWrapper/TabWrapper";
import Button from "@mui/material/Button";
import SwitchWrapper from "../SwitchWrapper/SwitchWrapper";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import {
  useApiClientQuery,
  useApiClientMutation,
} from "../../hooks/useApiClient";

const EditOrganizations = () => {
  const location = useLocation();
  const [organizationData, setOrganizationData] = useState({
    forte_organization_id: "",
    forte_location_id: "",
  });
  const [country, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryLoading, setCountryLoading] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [OTPCode, setOTPCode] = useState("");
  const [showOTP, setShowOTP] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [ecomValidation, setEcomValidation] = useState({
    publicTokenMissing: false,
    privateTokenMissing: false,
  });
  const { t } = useCustomTranslation("Créer une organisation");

  const {
    showSuccessAlert,
    setShowSuccessAlert,
    messageSuccessAlert,
    setMessageSuccessAlert,
  } = useSuccessAlert();

  useEffect(() => {
    fetchOrganization();
    fetchCountries();
  }, []);

  const archiveOrganizationMutation = useApiClientMutation(
    api_endpoints.setArchiveOrganizationStatus,
    [`archive_organization_${organizationData.is_archived}`],
    (data) => {
      setMessageSuccessAlert(t(data));
      setShowSuccessAlert(true);
      fetchOrganization();
    }
  );

  const handleArchiveOrganization = async () => {
    const organizationArchiveData = {
      organization_id: organizationData._id,
      archive_status: !organizationData.is_archived,
    };

    archiveOrganizationMutation.mutate(organizationArchiveData);
  };

  const fetchCountries = async () => {
    setCountryLoading(true);

    try {
      let response = await api_endpoints.getCountries();
      if (response.status === 200) {
        const formattedCountries = response.data.map((country) => ({
          id: country._id,
          label: `${country.name} (${country.code})`,
        }));
        setCountries(formattedCountries);
      }
    } catch (error) {
      console.error("Error fetching Countries", error);
    } finally {
      setCountryLoading(false);
    }
  };

  const fetchUserOTP = async (userData) => {
    if (userData.is_verified === false) {
      try {
        let otpResponse = await api_endpoints.checkUserOTP(userData.email);
        if (otpResponse.status === 200) {
          setShowOTP(true);
          setOTPCode(otpResponse.data.otp);
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleSubmit = async (e, formData) => {
    e.preventDefault();

    const ecomEnabled = !!formData.useEcomTokens;
    const publicTokenAvailable = !!(formData.ecomPublicToken || "").trim() || !!formData.hasEcomPublicToken;
    const privateTokenAvailable = !!(formData.ecomPrivateToken || "").trim() || !!formData.hasEcomPrivateToken;

    const publicTokenMissing = ecomEnabled && !publicTokenAvailable;
    const privateTokenMissing = ecomEnabled && !privateTokenAvailable;

    if (publicTokenMissing || privateTokenMissing) {
      setEcomValidation({ publicTokenMissing, privateTokenMissing });
      return;
    }

    setEcomValidation({ publicTokenMissing: false, privateTokenMissing: false });
    setLoading(true);
    const postData = { ...formData };
    try {
      const resolvedCountryId =
        selectedCountry?.id ??
        selectedCountry?._id ??
        postData?.country?._id ??
        postData?.country?.id ??
        postData?.country ??
        organizationData?.country?._id ??
        organizationData?.country?.id ??
        null;

      let response = await api_endpoints.editOrganization(
        postData,
        location.state.organization_id,
        resolvedCountryId
      );

      if (response.status === 200) {
        setMessageSuccessAlert(response.data);
        setShowSuccessAlert(true);
        navigate(getPageFromName("Organisations").path);
      }

      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.error(e);
    }
  };

  const fetchOrganization = async () => {
    setLoading(true);
    let response = await api_endpoints.getOrganization(
      location.state.organization_id
    );

    if (response.status === 200) {
      let organizationData = response.data;
      organizationData = {
        ...response.data,
      };

      organizationData.forte_organization_id =
        organizationData.forte_organization_id !== undefined &&
        organizationData.forte_organization_id !== null
          ? organizationData.forte_organization_id.toString()
          : "";

      organizationData.forte_location_id =
        organizationData.forte_location_id !== undefined &&
        organizationData.forte_location_id !== null
          ? organizationData.forte_location_id.toString()
          : "";

      if (organizationData.owner.is_verified === false) {
        fetchUserOTP(organizationData.owner);
      }

      setOrganizationData(organizationData);
      if (response.data.country) {
        const countryData = response.data.country;
        const formattedCountry = {
          id: countryData._id,
          label: `${countryData.name} (${countryData.code})`,
        };
        setSelectedCountry(formattedCountry);
      }
    } else {
      setLoadingError(true);
    }

    setLoading(false);
  };

  const handleTabChange = (event, value) => {
    setTabValue(value);
  };

  const handleDeleteOrganization = async (event) => {
    const confirmed = window.confirm(t("org_confirm_delete"));
    if (confirmed) {
      try {
        let deleteResponse = await api_endpoints.deleteOrganization(
          location.state.organization_id
        );
        if (deleteResponse.status === 200) {
          setMessageSuccessAlert(deleteResponse.data);
          setShowSuccessAlert(true);
          navigate(getPageFromName("Organisations").path);
        }
      } catch (e) {
        console.error(e);
      }
    } else {
      // Cancelled deletion
    }
  };

  const handleCountrySelect = (event, value) => {
    setSelectedCountry(value);
  };

  return (
    <LoadingContainer loading={loading} error={loadingError}>
      <div className="flex flex-1 flex-col mt-4">
        <div className="flex mb-4 justify-between">
          <div className="flex-grow ml-2">
            {organizationData?.is_archived ? (
              <Chip color="error" label="Archivé" />
            ) : (
              <Chip color="success" label="Actif" />
            )}
          </div>
          <div className="flex space-x-4">
            <Button
              color="warning"
              variant="contained"
              className="add_to_archive_button"
              onClick={handleArchiveOrganization}
            >
              {organizationData?.is_archived
                ? t("archive_toggle_remove")
                : t("archive_toggle_add")}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={(event) => handleDeleteOrganization(event)}
            >
              {t("org_delete_button_text")}{" "}
            </Button>
          </div>
        </div>

        <FormController initialValues={organizationData}>
          {({ value, handleChange, getFormFields }) => {
            const ecomEnabled = !!value?.useEcomTokens;
            const publicTokenAvailable = !!(value?.ecomPublicToken || "").trim() || !!value?.hasEcomPublicToken;
            const privateTokenAvailable = !!(value?.ecomPrivateToken || "").trim() || !!value?.hasEcomPrivateToken;
            const publicTokenMissing = ecomEnabled && !publicTokenAvailable;
            const privateTokenMissing = ecomEnabled && !privateTokenAvailable;

            return (
            <form onSubmit={(e) => handleSubmit(e, getFormFields())}>
              <div className="container-white flex-col">
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label={t("details_tab")} />
                  <Tab label={t("clover_settings_tab")} />
                  <Tab label={t("forte_settings_tab", "Forte Settings")} />
                  {/* <Tab label="Paramètres VoPay" /> */}
                </Tabs>
                <Divider />
                <TabWrapper
                  className="flex flex-1 space-y-6 flex-col mt-4"
                  index={0}
                  value={tabValue}
                >
                  <TextField
                    fullWidth
                    required
                    autoComplete="off"
                    variant="outlined"
                    label={t("org_legalname")}
                    value={value.legal_name}
                    name="legal_name"
                    onChange={handleChange}
                    disabled={organizationData?.is_archived}
                  />
                  <Autocomplete
                    options={country}
                    getOptionLabel={(option) =>
                      option && option.label ? option.label : ""
                    }
                    onChange={handleCountrySelect}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          countryLoading
                            ? t("org_country_select_loading")
                            : t("org_country_select")
                        }
                        required
                      />
                    )}
                    value={selectedCountry}
                    isOptionEqualToValue={(option, value) =>
                      option.id === (value ? value.id : -1)
                    }
                    renderTags={() => null}
                    disableClearable
                    name="country"
                    disabled={countryLoading || organizationData?.is_archived}
                  />
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    label={t("org_adress")}
                    autoComplete="off"
                    value={value.address}
                    name="address"
                    onChange={handleChange}
                    disabled={organizationData?.is_archived}
                  />
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    label={t("org_telephone")}
                    autoComplete="off"
                    value={value.phone}
                    name="phone"
                    onChange={handleChange}
                    disabled={organizationData?.is_archived}
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t("org_federal_tax_nbr")}
                    autoComplete="off"
                    value={value.federal_tax_number}
                    name="federal_tax_number"
                    onChange={handleChange}
                    disabled={organizationData?.is_archived}
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t("org_tax_receipt")}
                    autoComplete="off"
                    value={value.tax_receipt}
                    name="tax_receipt"
                    onChange={handleChange}
                    disabled={organizationData?.is_archived}
                  />
                  <SwitchWrapper
                    label={t("org_is_surcharge_active")}
                    checked={value.is_surcharge_active}
                    required={value.is_surcharge_active}
                    name="is_surcharge_active"
                    onChange={handleChange}
                    disabled={organizationData?.is_archived}
                  >
                    <TextField
                      required
                      fullWidth
                      type="number"
                      variant="outlined"
                      label={t("org_surcharge_fee")}
                      placeholder={t("org_surcharge_fee")}
                      name="surcharge_rate"
                      onChange={(e) => {
                        e.target.value /= 100;
                        handleChange(e);
                      }}
                      value={value.surcharge_rate * 100}
                      InputProps={{
                        startAdornment: <div className="mr-4">%</div>,
                      }}
                      disabled={organizationData?.is_archived}
                    />
                  </SwitchWrapper>
                  {value.is_surcharge_active && (
                    <SwitchWrapper
                      label={t("org_is_surcharge_mandatory")}
                      checked={value.is_surcharge_mandatory}
                      name="is_surcharge_mandatory"
                      onChange={handleChange}
                    />
                  )}
                  <Divider />
                  <div className="text-lg">{t("org_contact_header")}</div>
                  {showOTP ? (
                    <div className="text-md font-semibold text-red-600">
                      {t("org_otp_code")} : {OTPCode}
                    </div>
                  ) : null}
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t("org_contact_firstname")}
                    autoComplete="off"
                    value={value?.owner?.first_name}
                    name="owner.first_name"
                    onChange={handleChange}
                    disabled
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t("org_contact_lastname")}
                    autoComplete="off"
                    value={value?.owner?.last_name}
                    name="owner.last_name"
                    onChange={handleChange}
                    disabled
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t("org_contact_email")}
                    autoComplete="off"
                    value={value?.owner?.email}
                    name="owner.email"
                    onChange={handleChange}
                    disabled
                  />
                </TabWrapper>
                <TabWrapper
                  index={1}
                  value={tabValue}
                  className="flex flex-1 space-y-6 flex-col mt-4"
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t("org_merchant_id")}
                    autoComplete="off"
                    value={value?.merchant_id}
                    name="merchant_id"
                    onChange={handleChange}
                  />
                  <Divider />
                  <SwitchWrapper
                    label={t("org_use_ecom_tokens", "Use Ecom Tokens")}
                    checked={!!value?.useEcomTokens}
                    name="useEcomTokens"
                    onChange={handleChange}
                    disabled={organizationData?.is_archived}
                  >
                    <TextField
                      fullWidth
                      required={ecomEnabled}
                      error={ecomValidation.publicTokenMissing && publicTokenMissing}
                      variant="outlined"
                      label={t("org_ecom_public_token", "Ecom Public Token")}
                      autoComplete="off"
                      type="password"
                      value={value?.ecomPublicToken || ""}
                      name="ecomPublicToken"
                      onChange={handleChange}
                      placeholder={
                        value?.hasEcomPublicToken
                          ? t("org_ecom_public_token_placeholder_existing", "Enter new token to replace existing one")
                          : t("org_ecom_public_token_placeholder", "Enter Clover ecom public API token")
                      }
                      helperText={
                        ecomValidation.publicTokenMissing && publicTokenMissing
                          ? t("org_ecom_public_token_required", "Ecom public token is required when ecom mode is enabled.")
                          : value?.hasEcomPublicToken && !value?.ecomPublicToken
                            ? t("org_ecom_public_token_saved", "An ecom public token is already saved. Leave blank to keep it.")
                            : t("org_ecom_public_token_help", "Used as the PAKMS apikey for card tokenization.")
                      }
                      disabled={organizationData?.is_archived}
                    />
                    <TextField
                      fullWidth
                      required={ecomEnabled}
                      error={ecomValidation.privateTokenMissing && privateTokenMissing}
                      variant="outlined"
                      label={t("org_ecom_private_token", "Ecom Private Token")}
                      autoComplete="off"
                      type="password"
                      value={value?.ecomPrivateToken || ""}
                      name="ecomPrivateToken"
                      onChange={handleChange}
                      placeholder={
                        value?.hasEcomPrivateToken
                          ? t("org_ecom_private_token_placeholder_existing", "Enter new token to replace existing one")
                          : t("org_ecom_private_token_placeholder", "Enter Clover ecom private API token")
                      }
                      helperText={
                        ecomValidation.privateTokenMissing && privateTokenMissing
                          ? t("org_ecom_private_token_required", "Ecom private token is required when ecom mode is enabled.")
                          : value?.hasEcomPrivateToken && !value?.ecomPrivateToken
                            ? t("org_ecom_private_token_saved", "An ecom private token is already saved. Leave blank to keep it.")
                            : t("org_ecom_private_token_help", "Used as Bearer token for charges, refunds, and PAKMS calls.")
                      }
                      disabled={organizationData?.is_archived}
                    />
                  </SwitchWrapper>
                </TabWrapper>
                <TabWrapper
                  index={2}
                  value={tabValue}
                  className="flex flex-1 space-y-6 flex-col mt-4"
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t(
                      "forte_organization_id_label",
                      "Forte Organization ID"
                    )}
                    autoComplete="off"
                    value={value?.forte_organization_id ?? ""}
                    name="forte_organization_id"
                    onChange={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                      handleChange(e);
                    }}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    disabled={organizationData?.is_archived}
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t(
                      "forte_location_id_label",
                      "Forte Location ID"
                    )}
                    autoComplete="off"
                    value={value?.forte_location_id ?? ""}
                    name="forte_location_id"
                    onChange={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                      handleChange(e);
                    }}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    disabled={organizationData?.is_archived}
                  />
                </TabWrapper>
              </div>
              <div className="flex flex-1 mt-2 justify-end space-x-4">
                <CancelButton
                  disabled={loading}
                  label={t("org_cancel_button")}
                />
                <ButtonLoading
                  loading={loading}
                  variant="contained"
                  type="submit"
                  disabled={organizationData?.is_archived}
                >
                  {t("org_save_button")}
                </ButtonLoading>
              </div>
            </form>
          );
          }}
        </FormController>
      </div>
    </LoadingContainer>
  );
};

export default EditOrganizations;
